/** Canvas-based screen distortion effects: flash, vignette, color wash, tear lines, chromatic aberration. */
import Phaser from 'phaser';
import { VfxPrimitive, VfxPrimitiveConfig } from './VfxPrimitive';
import { CRISP_SCALE, ensureChromaticTexture, ensureVignetteTexture } from '../../ui/CrispUtil';

export interface ScreenDistortionConfig extends VfxPrimitiveConfig {
  type: 'flash' | 'vignette' | 'colorWash' | 'tear' | 'chromatic';
  alpha: number;
  durationMs: number;
  sustainMs?: number;
  tearLineCount?: number;
  chromaticOffset?: number;
  chromaticRadius?: number;
}

const DEFAULT_TEAR_LINE_COUNT = 4;
const DEFAULT_CHROMATIC_OFFSET = 4;
const DEFAULT_CHROMATIC_RADIUS = 64;

export class ScreenDistortion extends VfxPrimitive<ScreenDistortionConfig> {
  private overlay: Phaser.GameObjects.Rectangle | null = null;
  private vignetteImg: Phaser.GameObjects.Image | null = null;
  private chromaticImg: Phaser.GameObjects.Image | null = null;
  private tearLines!: Phaser.GameObjects.Rectangle[];

  protected allocatePool(): void {
    this.tearLines = [];
    const cam = this.scene.cameras.main;
    const { config } = this;
    const highDepth = Number.MAX_SAFE_INTEGER;

    switch (config.type) {
      case 'flash':
        this.overlay = this.scene.add
          .rectangle(cam.centerX, cam.centerY, cam.width, cam.height, config.tint)
          .setAlpha(0)
          .setDepth(highDepth)
          .setScrollFactor(0)
          .setVisible(false);
        break;

      case 'vignette': {
        const texKey = ensureVignetteTexture(this.scene, cam.width, cam.height);
        this.vignetteImg = this.scene.add
          .image(cam.centerX, cam.centerY, texKey)
          .setScale(1 / CRISP_SCALE)
          .setTint(config.tint)
          .setAlpha(0)
          .setDepth(highDepth)
          .setScrollFactor(0)
          .setVisible(false);
        break;
      }

      case 'colorWash':
        this.overlay = this.scene.add
          .rectangle(cam.centerX, cam.centerY, cam.width, cam.height, config.tint)
          .setAlpha(0)
          .setDepth(highDepth)
          .setScrollFactor(0)
          .setBlendMode(Phaser.BlendModes.MULTIPLY)
          .setVisible(false);
        break;

      case 'tear': {
        const count = config.tearLineCount ?? DEFAULT_TEAR_LINE_COUNT;
        for (let i = 0; i < count; i++) {
          const line = this.scene.add
            .rectangle(cam.centerX, 0, cam.width, 2, config.tint)
            .setAlpha(0)
            .setDepth(highDepth)
            .setScrollFactor(0)
            .setVisible(false);
          this.tearLines.push(line);
        }
        break;
      }

      case 'chromatic': {
        const radius = config.chromaticRadius ?? DEFAULT_CHROMATIC_RADIUS;
        const offset = config.chromaticOffset ?? DEFAULT_CHROMATIC_OFFSET;
        const texKey = ensureChromaticTexture(this.scene, radius, offset);
        this.chromaticImg = this.scene.add
          .image(cam.centerX, cam.centerY, texKey)
          .setScale(1 / CRISP_SCALE)
          .setAlpha(0)
          .setDepth(highDepth)
          .setScrollFactor(0)
          .setBlendMode(Phaser.BlendModes.ADD)
          .setVisible(false);
        break;
      }
    }
  }

  play(_x: number, _y: number, _depth: number): void {
    const { config } = this;

    switch (config.type) {
      case 'flash':
        this.playFlash();
        break;
      case 'vignette':
        this.playVignette();
        break;
      case 'colorWash':
        this.playColorWash();
        break;
      case 'tear':
        this.playTear();
        break;
      case 'chromatic':
        this.playChromatic();
        break;
    }
  }

  stop(): void {
    const { config } = this;

    if (config.type === 'vignette' && this.vignetteImg) {
      this.scene.tweens.killTweensOf(this.vignetteImg);
      this.scene.tweens.add({
        targets: this.vignetteImg,
        alpha: 0,
        duration: config.durationMs,
        ease: 'Quad.easeOut',
        onComplete: () => this.vignetteImg?.setVisible(false),
      });
    }

    if (config.type === 'colorWash' && this.overlay) {
      this.scene.tweens.killTweensOf(this.overlay);
      this.scene.tweens.add({
        targets: this.overlay,
        alpha: 0,
        duration: config.durationMs,
        ease: 'Quad.easeOut',
        onComplete: () => this.overlay?.setVisible(false),
      });
    }
  }

  destroy(): void {
    if (this.overlay) {
      this.scene.tweens.killTweensOf(this.overlay);
      this.overlay.destroy();
      this.overlay = null;
    }
    if (this.vignetteImg) {
      this.scene.tweens.killTweensOf(this.vignetteImg);
      this.vignetteImg.destroy();
      this.vignetteImg = null;
    }
    if (this.chromaticImg) {
      this.scene.tweens.killTweensOf(this.chromaticImg);
      this.chromaticImg.destroy();
      this.chromaticImg = null;
    }
    for (const line of this.tearLines) {
      this.scene.tweens.killTweensOf(line);
      line.destroy();
    }
    this.tearLines.length = 0;
    super.destroy();
  }

  private playFlash(): void {
    if (!this.overlay) return;
    this.scene.tweens.killTweensOf(this.overlay);
    this.overlay.setAlpha(this.config.alpha).setVisible(true);
    this.scene.tweens.add({
      targets: this.overlay,
      alpha: 0,
      duration: this.config.durationMs,
      ease: 'Quad.easeOut',
      onComplete: () => this.overlay?.setVisible(false),
    });
  }

  private playVignette(): void {
    if (!this.vignetteImg) return;
    const { config } = this;
    this.scene.tweens.killTweensOf(this.vignetteImg);
    this.vignetteImg.setAlpha(0).setVisible(true);

    const fadeInDuration = Math.min(config.durationMs * 0.3, 100);
    const sustainMs = config.sustainMs ?? 0;

    this.scene.tweens.add({
      targets: this.vignetteImg,
      alpha: config.alpha,
      duration: fadeInDuration,
      ease: 'Quad.easeIn',
      onComplete: () => {
        if (sustainMs > 0) {
          this.scene.time.delayedCall(sustainMs, () => {
            if (!this.vignetteImg) return;
            this.scene.tweens.add({
              targets: this.vignetteImg,
              alpha: 0,
              duration: config.durationMs - fadeInDuration,
              ease: 'Quad.easeOut',
              onComplete: () => this.vignetteImg?.setVisible(false),
            });
          });
        } else {
          if (!this.vignetteImg) return;
          this.scene.tweens.add({
            targets: this.vignetteImg,
            alpha: 0,
            duration: config.durationMs - fadeInDuration,
            ease: 'Quad.easeOut',
            onComplete: () => this.vignetteImg?.setVisible(false),
          });
        }
      },
    });
  }

  private playColorWash(): void {
    if (!this.overlay) return;
    const { config } = this;
    this.scene.tweens.killTweensOf(this.overlay);
    this.overlay.setAlpha(0).setVisible(true);

    const fadeInDuration = Math.min(config.durationMs * 0.3, 100);
    const sustainMs = config.sustainMs ?? 0;

    this.scene.tweens.add({
      targets: this.overlay,
      alpha: config.alpha,
      duration: fadeInDuration,
      ease: 'Quad.easeIn',
      onComplete: () => {
        if (sustainMs > 0) {
          this.scene.time.delayedCall(sustainMs, () => {
            if (!this.overlay) return;
            this.scene.tweens.add({
              targets: this.overlay,
              alpha: 0,
              duration: config.durationMs - fadeInDuration,
              ease: 'Quad.easeOut',
              onComplete: () => this.overlay?.setVisible(false),
            });
          });
        } else {
          if (!this.overlay) return;
          this.scene.tweens.add({
            targets: this.overlay,
            alpha: 0,
            duration: config.durationMs - fadeInDuration,
            ease: 'Quad.easeOut',
            onComplete: () => this.overlay?.setVisible(false),
          });
        }
      },
    });
  }

  private playTear(): void {
    const cam = this.scene.cameras.main;
    for (const line of this.tearLines) {
      this.scene.tweens.killTweensOf(line);
      const y = Math.random() * cam.height;
      line.setPosition(cam.centerX, y).setAlpha(this.config.alpha).setVisible(true);

      this.scene.tweens.add({
        targets: line,
        alpha: 0,
        y: y + (Math.random() - 0.5) * 10,
        duration: this.config.durationMs,
        ease: 'Quad.easeOut',
        onComplete: () => line.setVisible(false),
      });
    }
  }

  private playChromatic(): void {
    if (!this.chromaticImg) return;
    this.scene.tweens.killTweensOf(this.chromaticImg);
    this.chromaticImg
      .setAlpha(this.config.alpha)
      .setScale(1 / CRISP_SCALE)
      .setVisible(true);

    this.scene.tweens.add({
      targets: this.chromaticImg,
      alpha: 0,
      scaleX: (1 / CRISP_SCALE) * 1.5,
      scaleY: (1 / CRISP_SCALE) * 1.5,
      duration: this.config.durationMs,
      ease: 'Quad.easeOut',
      onComplete: () => this.chromaticImg?.setVisible(false),
    });
  }
}
